import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../icons/Icon';
import iconsList from '../icons/iconsList';
import useClickOutside from '../../../hooks/useClickOutside';
import ScrollLTR from '../scrollLTR/ScrollLTR';
import Tooltip from '../tooltip/Tooltip';
import classes from './Menu.module.css';

const isNavActive = (pathname, to) =>
    pathname === to || pathname.startsWith(`${to}/`);

const Menu = ({ children, className, overlayBreakpoint, forceCompact = false, showIcon = true }) => {

    const location = useLocation();
    const [isOverlay, setIsOverlay] = useState(window.innerWidth < overlayBreakpoint);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const menuRef = useRef();

    useEffect(() => {
        if (overlayBreakpoint) {
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }
    }, [overlayBreakpoint]);

    const handleResize = () => {
        if (overlayBreakpoint) {
            setIsOverlay(window.innerWidth < overlayBreakpoint);
            setIsPopupOpen(false);
        }
    };

    useClickOutside(menuRef, () => {
        if (menuRef.current) {
            setIsPopupOpen(false)
        }
    });

    //compact menu (icons only)
    const renderCompactMenu = () => (
        <nav className={`${classes.Menu} ${classes.Compact} ${classes.Overlay} ${className || ''}`} >
            <ScrollLTR height="100%" dropClass={classes.MenuScroll} downDropStyle={classes.MenuContent}>
                {React.Children.map(children, (child) =>
                    React.isValidElement(child)
                        ? React.cloneElement(child, {
                            isActive: isNavActive(location.pathname, child.props.to),
                            showLabel: false,
                            showIcon,
                            onIconClick: () => {if (!forceCompact) setIsPopupOpen(true)}
                        })
                        : null
                )}
            </ScrollLTR>
        </nav>
    );

    //full menu popup
    const renderPopupMenu = () => (
        <>
            <nav className={`${classes.Menu} ${classes.Full} ${classes.Open} ${classes.Overlay} ${className || ''}`} >
                <ScrollLTR height="100%" dropClass={classes.MenuScroll} downDropStyle={classes.MenuContent}>
                    {React.Children.map(children, (child) =>
                        React.isValidElement(child)
                            ? React.cloneElement(child, {
                                isActive: isNavActive(location.pathname, child.props.to),
                                showLabel: true,
                                showIcon,
                                onIconClick: () => setIsPopupOpen(true)
                            })
                            : null
                    )}
                </ScrollLTR>
            </nav>
        </>
    );

    //regular menu
    const renderFullMenu = () => (
        <nav className={`${classes.Menu} ${classes.Full} ${classes.Inline} ${className || ''}`}>
            <ScrollLTR height="100%" dropClass={classes.MenuScroll} downDropStyle={classes.MenuContent}>
                {React.Children.map(children, (child) =>
                    React.isValidElement(child)
                        ? React.cloneElement(child, {
                            isActive: isNavActive(location.pathname, child.props.to),
                            showLabel: true,
                            showIcon
                        })
                        : null
                )}
            </ScrollLTR>
        </nav>
    );

    return (
        (isOverlay || forceCompact) ?
            <div ref={menuRef} className={classes.MenuWrapper}>
                {renderCompactMenu()}
                {isPopupOpen && renderPopupMenu()}
            </div>
            :
            renderFullMenu()
    );
};

const Nav = ({ title, icon, to, isActive, showLabel = true, showIcon = true, onIconClick }) => {

    const navigate = useNavigate();

    const handleIconClick = () => {
        navigate(to);
        onIconClick && onIconClick();
    };

    return (
        <Tooltip showTooltip={!showLabel} text={title} position="LeftCenter">
            <div className={`${classes.NavItem} ${isActive ? classes.Active : ''}`} onClick={handleIconClick}>
                {showIcon && (
                    <span className={classes.Icon} >
                        <Icon size="20px" name={iconsList[icon]} color={isActive ? "#6300FF" : "#727272"} />
                    </span>
                )}
                {showLabel && <span className={classes.Label}>{title}</span>}
            </div>
        </Tooltip>
    );
};

export { Menu, Nav };